import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FiCalendar, FiMapPin, FiExternalLink } from 'react-icons/fi';

const ExperienceSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.8rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #00ff00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #00ff00;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 2.5rem;
  }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #00ff00, #3b82f6);
    transform: translateX(-50%);

    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  margin-bottom: 3rem;
  display: flex;
  align-items: center;

  &:nth-child(odd) {
    flex-direction: row;

    @media (max-width: 768px) {
      flex-direction: row;
    }
  }

  &:nth-child(even) {
    flex-direction: row-reverse;

    @media (max-width: 768px) {
      flex-direction: row;
    }
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  max-width: 45%;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(0, 255, 0, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: relative;

  @media (max-width: 768px) {
    max-width: calc(100% - 60px);
    margin-left: 60px;
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    max-width: calc(100% - 50px);
    margin-left: 50px;
    padding: 1.2rem;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    width: 0;
    height: 0;
    border-style: solid;
    transform: translateY(-50%);

    ${props => props.isEven ? `
      right: -10px;
      border-width: 10px 0 10px 10px;
      border-color: transparent transparent transparent rgba(0, 255, 0, 0.2);
    ` : `
      left: -10px;
      border-width: 10px 10px 10px 0;
      border-color: transparent rgba(0, 255, 0, 0.2) transparent transparent;
    `}

    @media (max-width: 768px) {
      left: -10px;
      right: auto;
      border-width: 10px 10px 10px 0;
      border-color: transparent rgba(0, 255, 0, 0.2) transparent transparent;
    }
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 20px;
  height: 20px;
  background: linear-gradient(45deg, #00ff00, #3b82f6);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 4px solid #0a0a0a;
  z-index: 2;

  @media (max-width: 768px) {
    left: 20px;
    transform: translate(-50%, -50%);
  }
  
  @media (max-width: 480px) {
    left: 15px;
    width: 16px;
    height: 16px;
    border: 3px solid #0a0a0a;
  }
`;

const JobTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CompanyName = styled.h4`
  font-size: 1.1rem;
  font-weight: 500;
  color: #00ff00;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const JobMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const JobDescription = styled.p`
  color: #cbd5e1;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    line-height: 1.7;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.8;
    margin-bottom: 1rem;
  }
`;

const Technologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TechTag = styled.span`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  color: #00ff00;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #00ff00;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const Experience = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const experiences = [
    {
      title: 'Web Developer',
      company: 'Next Edge Solutions',
      period: 'March 2025 - Present',
      location: 'Lahore, Pakistan',
      description: 'Design and develop innovative, high-performance digital solutions that empower businesses to achieve their goals. Leverage expertise in modern web development technologies to deliver efficient, scalable, and user-focused applications in a fully remote environment.',
      technologies: ['React', 'Node.js', 'JavaScript', 'MongoDB', 'Express.js', 'Web Development'],
      link: 'https://dayar-web.vercel.app/'
    },
    {
      title: 'AI Enhanced Web Development Summer Internship',
      company: 'Nexium',
      period: 'July 2025 - August 2025',
      location: 'Lahore, Pakistan',
      description: 'Independently designed and deployed AI-enhanced, full-stack web applications using modern technologies, culminating in the creation of Resumly, an AI-powered resume tailoring and export platform. This project demonstrated expertise in full-stack development, UI/UX design, and the integration of advanced AI features to enhance functionality and user experience.',
      technologies: ['Next.js', 'React', 'TypeScript', 'AI/ML', 'Supabase', 'MongoDB'],
      link: 'https://resumly-tailor.vercel.app/'
    },
    {
      title: 'MERN Stack Summer Fellowship',
      company: 'Fluxxion',
      period: 'June 2025 - August 2025',
      location: 'Lahore, Pakistan',
      description: 'Gained hands-on experience in full-stack development by building scalable, high-performance web applications using MongoDB, Express.js, React, and Node.js. Applied best practices in API development, database design, and front-end optimization, while strengthening skills in version control, deployment, and delivering production-ready solutions in a fast-paced, collaborative environment.',
      technologies: ['MERN Stack', 'MongoDB', 'Express.js', 'React', 'Node.js', 'API Development'],
      link: 'https://mingle-me.vercel.app/'
    }
  ];

  return (
    <ExperienceSection id="experience" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Work Experience
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          My professional journey and the companies I've had the pleasure to work with
        </SectionSubtitle>

        <Timeline>
          {experiences.map((experience, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.2 }}
            >
              <TimelineDot />
              <TimelineContent isEven={index % 2 === 1}>
                <JobTitle>{experience.title}</JobTitle>
                <CompanyName>{experience.company}</CompanyName>
                
                <JobMeta>
                  <MetaItem>
                    <FiCalendar />
                    {experience.period}
                  </MetaItem>
                  <MetaItem>
                    <FiMapPin />
                    {experience.location}
                  </MetaItem>
                </JobMeta>
                
                <JobDescription>{experience.description}</JobDescription>
                
                <Technologies>
                  {experience.technologies.map((tech, techIndex) => (
                    <TechTag key={techIndex}>{tech}</TechTag>
                  ))}
                </Technologies>
                
                <ProjectLink href={experience.link} target="_blank" rel="noopener noreferrer">
                  View Project <FiExternalLink />
                </ProjectLink>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Container>
    </ExperienceSection>
  );
};

export default Experience;
